import type {AppRouteRecordRaw} from "#src/router/types"; // Import type definition

import {IconReader} from "#src/components/iconReader/IconReader";
import ContainerLayout from "#src/layout/container-layout"; // Assuming ContainerLayout is the main layout component
// Using TwitterOutlined as the icon
import {createElement, lazy} from "react";
// Lazy Load the Vessel Page Component
const PortsPage = lazy(() => import("#src/features/Ports"));
// const HolidayPage = lazy(() => import("#src/pages/holiday"));
// const VesselPage = lazy(() => import("#src/features/Vessels"));
const ClassificationsPage = lazy(() => import("#src/features/Classifications"));
const CompanyCategoriesPage = lazy(() => import("#src/features/CompanyCategories"));
const CompanySubcategoriesPage = lazy(() => import("#src/features/CompanySubcategories"));
const CompaniesPage = lazy(() => import("#src/features/Companies"));
const UOMPage = lazy(() => import("#src/features/UOM"));
const IMPAPage = lazy(() => import("#src/features/IMPA"));
const JobClassPage = lazy(() => import("#src/features/JobClass"));
const MaintenanceMethodPage = lazy(() => import("#src/features/MaintenanceMethod"));
const DefectTypePage = lazy(() => import("#src/features/DefectTypes"));
const WorkOrderClassPage = lazy(() => import("#src/features/WorkOrderClasses"));
const ComponentClassPage = lazy(() => import("#src/features/ComponentClasses"));
const ComponentTypePage = lazy(() => import("#src/features/ComponentTypes"));
const StockClassPage = lazy(() => import("#src/features/StockClasses"));
const StockTypePage = lazy(() => import("#src/features/StockTypes"));
// const StockPage = lazy(() => import("#src/features/Stocks"));
const LocationPage = lazy(() => import("#src/features/Locations"));
const StorePage = lazy(() => import("#src/features/Stores"));
const RankPage = lazy(() => import("#src/features/Ranks"));
const PropertyRangeTypePage = lazy(() => import("#src/features/PropertyRangeTypes"));
const PermitPage = lazy(() => import("#src/features/Permits"));
const PrioritiesPage = lazy(() => import("#src/features/Priorities"));
const BudgetCodesPage = lazy(() => import("#src/features/BudgetCodes"));
const ServiceGroupsPage = lazy(() => import("#src/features/ServiceGroups"));
const ReasonLabelsPage = lazy(() => import("#src/features/ReasonLabels"));
const DepartmentsPage = lazy(() => import("#src/features/Departments"));

/**
 * Vessel Route Definition based on AppRouteRecordRaw
 */
const routes: AppRouteRecordRaw[] = [
	{
		// Mandatory for NonIndexRouteObject
		path: "/reference",
		// Use the standard Container Layout for the main grouping route
		Component: ContainerLayout,
		// Route metadata
		handle: {
			// Title is used for the menu and page title
			title: "Ref.Data",
			// Icon for the sidebar menu
			icon: createElement(IconReader, { name: "file-text" }) as any as any as any,
			// Menu order (optional)
			order: 2,
			// Enables page caching (effective when tabs are enabled)
			keepAlive: true,
		},
		children: [
			{
				// Route metadata
				handle: {
					// Title for consistency in tabs/breadcrumbs
					title: "Vessel",
					// Hiding the child route from the menu (since the parent route acts as the main menu item)
					// hideInMenu: true,
				},
				children: [

					{
						path: "/reference/vessel/classification",
						Component: ClassificationsPage,
						handle: {
							title: "Classification",
						},
					},
				],
			},
			{
				// Route metadata
				handle: {
					// Title for consistency in tabs/breadcrumbs
					title: "Operation",
					// Hiding the child route from the menu (since the parent route acts as the main menu item)
					// hideInMenu: true,
				},
				children: [
					{
						// This is the index route of /eventDate, path must be empty or relative
						path: "/reference/operation/port",
						// Component to render for the route
						Component: PortsPage,
						// Route metadata
						handle: {
							// Title for consistency in tabs/breadcrumbs
							title: "Port",
							// Hiding the child route from the menu (since the parent route acts as the main menu item)
							// hideInMenu: true,
						},
					},

				],
			},
			{
				handle: {
					title: "Company",
				},
				children: [
					{
						path: "/reference/companies/company",
						Component: CompaniesPage,
						handle: {
							title: "Company",
						},
					},
					{
						path: "/reference/companies/categories",
						Component: CompanyCategoriesPage,
						handle: {
							title: "Categories",
						},
					},
					{
						path: "/reference/companies/subcategories",
						Component: CompanySubcategoriesPage,
						handle: {
							title: "SubCategories",
						},
					},
				],
			},
			{
				handle: {
					title: "Stock & Inventory",
				},
				children: [
					{path: "/reference/stock/uom", Component: UOMPage, handle: {title: "UOM"}},
					{ path: "/reference/stock/impa", Component: IMPAPage, handle: { title: "IMPA" }},
					{path: "/reference/stock/stock-class", Component: StockClassPage, handle: { title: "Stock Class" }},
					{ path: "/reference/stock/stock-type", Component: StockTypePage, handle: { title: "Stock Type" }},
					// { path: "/reference/stock/stock", Component: StockPage, handle: { title: "Stock" }},
					{ path: "/reference/stock/location", Component: LocationPage, handle: { title: "Location" }},
					{ path: "/reference/stock/store", Component: StorePage, handle: { title: "Store" }},
				],
			},
			{
				handle: {
					title: "PMS",
				},
				children: [
					{path: "/reference/pms/property-range-type", Component: PropertyRangeTypePage, handle: { title: "Property Range Types" }},
					{path: "/reference/pms/permit", Component: PermitPage, handle: { title: "Permits" }},
					{
						path: "/reference/pms/job-class",
						Component: JobClassPage,
						handle: { title: "Job Class" },
					},
					{
						path: "/reference/pms/maintenance-method",
						Component: MaintenanceMethodPage,
						handle: { title: "Maintenance Method" },
					},
					{
						path: "/reference/pms/defect-type",
						Component: DefectTypePage,
						handle: { title: "Defect Type" },
					},
					{
						path: "/reference/pms/work-order-class",
						Component: WorkOrderClassPage,
						handle: { title: "Work Order Class" },
					},
					{
						path: "/reference/pms/component-class",
						Component: ComponentClassPage,
						handle: { title: "Component Class" },
					},
					{
						path: "/reference/pms/component-type",
						Component: ComponentTypePage,
						handle: { title: "Component Type" },
					},
				],
		},
			{
				handle: { title: "Crew" },
				children: [
					{path: "/reference/crew/rank", Component: RankPage, handle: { title: "Rank" }},
				],
			},
			{
				handle: { title: "Indent" },
				children: [
					{path: "/reference/indent/priorities", Component: PrioritiesPage, handle: { title: "Priorities" }},
					{path: "/reference/indent/departments", Component: DepartmentsPage, handle: { title: "Departments" }},
					{path: "/reference/indent/budget-codes", Component: BudgetCodesPage, handle: { title: "Budget Codes" }},
					{path: "/reference/indent/service-groups", Component: ServiceGroupsPage, handle: { title: "Service Groups" }},
					{path: "/reference/indent/reason-labels", Component: ReasonLabelsPage, handle: { title: "Reason Labels" }},
				],
			},
		],
	},
	// {
	// 	// Mandatory for NonIndexRouteObject
	// 	path: "/pms",
	// 	// Use the standard Container Layout for the main grouping route
	// 	Component: ContainerLayout,
	// 	// Route metadata
	// 	handle: {
	// 		// Title is used for the menu and page title
	// 		title: "PMS",
	// 		// Icon for the sidebar menu
	// 		icon: createElement(IconReader, { name: "file-text" }) as any as any,
	// 		// Menu order (optional)
	// 		order: 3,
	// 		// Enables page caching (effective when tabs are enabled)
	// 		keepAlive: true,
	// 	},
	// 	children: [
	// 	],
	// },
];

export default routes;
